import type {ApiErrorBody, ApiErrorHandler, RequestError, ReferencedObject} from "@/types/errors";
import type {ToastHandle} from "@/types/toast";
import {toastInstance} from "@/components/toast/Tostify";
import {loadTranslationObjectFiled} from "@/utils/ApiUti";
import {TranslationType} from "@/type/TranslationType";
import * as Sentry from '@sentry/vue';
import router from "@/router/index";
import {useUserStore} from "@/stores/userStore";

export const errorUtil = {
    catchErrors
};

async function catchErrors(error: unknown, toast: ToastHandle | null = null) {
    if (!(error as RequestError).response) {
        Sentry.captureException(error, {
            tags: {location: "catchErrors", reason: "No response"},
            extra: {
                hint: "No response received from the server",
                message: (error as RequestError).message,
                stack: (error as RequestError).stack,
                requestData: (error as RequestError).config?.data ? (JSON.parse(JSON.stringify((error as RequestError).config!.data)) as unknown) : null
            }
        });

        return "No response received from the server.";
    }

    const {status, data} = (error as RequestError).response!;
    const config = (error as RequestError).config || {};
    const url = config.url || '';
    const method = (config.method || '').toUpperCase();
    const key = `${method} ${url}`;
    const json = JSON.parse(JSON.stringify(data || {})) as ApiErrorBody;
    const requestData = config.data ? (JSON.parse(JSON.stringify(config.data)) as typeof config.data) : null;

    if (json.error && json.error.type === "AuthenticationErrors" && json.error.code === 0) {
        const authErrorMessage = "Authentication error: token not found or invalid.";
        sendOrUpdateToast(toast, authErrorMessage, "Authentication Error");

        Sentry.captureException(error, {
            tags: {location: "catchErrors", status, errorType: "AuthenticationErrors"},
            extra: {
                hint: "Authentication error detected",
                endpoint: url,
                method,
                status,
                responseData: json,
                originalMessage: (error as RequestError).message,
                requestData,
                originalError: error,
            },
        });

        const userStore = useUserStore();
        userStore.logout();
        await router.push({name: 'Login'})

        return authErrorMessage;
    }

    let errorMessage = "An unknown error occurred.";
    let errorTitle = "ErrorHandler"

    if (status === 400) {
        const {message, title} = handleBadRequest400(json)!;
        if (message) {
            errorMessage = message;
            errorTitle = title
            sendOrUpdateToast(toast, errorMessage, errorTitle)

            Sentry.captureException(error, {
                tags: {location: "catchErrors", status: 400},
                extra: {
                    hint: "Validation error (HTTP 400)",
                    endpoint: url,
                    method,
                    payload: json,
                    requestData,
                    originalError: error,
                },
            });

            return errorMessage;
        }
    }

    let matchedCategoryHandlers: Record<string, ApiErrorHandler> | null = null;
    for (const categoryPrefix of Object.keys(customHandlers)) {
        if (url.startsWith(categoryPrefix)) {
            matchedCategoryHandlers = await customHandlers[categoryPrefix];
            break;
        }
    }

    if (matchedCategoryHandlers) {
        let handlerFound = false;
        for (const [pattern, handler] of Object.entries(matchedCategoryHandlers)) {
            if (matchEndpoint(pattern, key)) {
                const customMessage = await handler(error, json, status);
                if (customMessage) {
                    errorMessage = customMessage;
                }
                handlerFound = true;
                break;
            }
        }
        if (!handlerFound) {
            errorMessage = defaultErrorMessage(status, json);
        }
    } else {
        errorMessage = defaultErrorMessage(status, json);
    }

    sendOrUpdateToast(toast, errorMessage, errorTitle)

    Sentry.captureException(error, {
        tags: {location: "catchErrors", status},
        extra: {
            hint: "API error occurred",
            endpoint: url,
            method,
            status,
            responseData: json,
            originalMessage: (error as RequestError).message,
            requestData,
            originalError: error,
        },
    });

    return errorMessage;
}

function sendOrUpdateToast(toast: { id: string; }|null, messageError: string, title: string|undefined) {
    if (toast) {
        toastInstance.update(toast.id, {
            title: title,
            description: messageError,
            type: 'error',
            duration: 9_000
        });
    } else {
        toastInstance.error(messageError, title);
    }
}

function defaultErrorMessage(status: number, json: ApiErrorBody) {
    if (status === 404 && json.kind && Array.isArray(json.objects)) {
        return `Could not find ${json.kind}:\n` + json.objects.join(', ');
    } else {
        const type = json.error?.type;
        const errorCode = json.error?.code;
        let message = "An unknown error occurred.";
        if (type && errorCode) {
            message = json.error!.message || `${type} - ${errorCode}`;
        }
        if (json.details?.path) {
            message += ` Path: <#>${json.details.path}</#>`;
        }
        switch (status) {
            case 400:
                return "Bad request. " + message;
            case 500:
                return "Internal server error.";
            default:
                return `Error ${status}: ` + message;
        }
    }
}

function handleBadRequest400(json: ApiErrorBody) {
    const details = json.details || json;

    switch (details.reason) {
        case 'invalid-json':
            return {title: 'Invalid JSON', message: `Invalid JSON: The request body could not be parsed.`};

        case 'missing-json':
            return {title: 'Invalid JSON', message: `Request body is required but was not provided.`}

        case 'missing-field':
            return {title: 'Missing field', message: `<#>Path:</#> ${json.path}\n<#>Expected type:</#> ${json.type}`}

        case 'invalid-type':
            return {
                title: 'Invalid type',
                message: `<#>Path:</#> ${json.path}\n<#>Expected:</#> ${details.expected}\n<#>Got:</#> ${details.got}`
            }

        case 'invalid-format':
            return {
                title: 'Invalid format',
                message: `<#>Path:</#> ${json.path}\n<#>Value:</#> ${details.value}\n<#>Expected format:</#> ${details.format}`
            }

        case 'invalid-parameter-format':
            return {
                title: 'Invalid parameter format',
                message: `<#>Path:</#> ${json.path}\n<#>Value:</#> ${details.value}\n<#>Expected format:</#> ${details.format}`
            }

        default:
            return null;
    }
}

function matchEndpoint(pattern: string, actual: string) {
    let escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    escapedPattern = escapedPattern.replace(/\\\{[^}]+\\\}/g, '[^/]+');

    const queryIndex = escapedPattern.indexOf('\\?');
    if (queryIndex !== -1) {
        const pathPart = escapedPattern.slice(0, queryIndex);
        const queryPart = escapedPattern.slice(queryIndex);

        const replacedQuery = queryPart.replace(/\\\{[^}]+\\\}/g, '.*');

        escapedPattern = pathPart + replacedQuery;
    }

    const regex = new RegExp(`^${escapedPattern}$`);

    return regex.test(actual);
}

function createSimpleErrorHandler(rules: ({ status: number; kind: string; message: string; condition?: undefined; }|{ status: number; condition: string; message: string; kind?: undefined; })[]): ApiErrorHandler {
    return (error, json, status) => {
        const details = json.details || json;
        const {kind, objects, condition, reason} = details;

        for (const rule of rules) {
            if (status === rule.status && condition === rule.condition && condition !== undefined) {
                return rule.message + ':\n' + `- ${reason}`;
            }

            if (status === rule.status && kind === rule.kind) {
                return rule.message.replace('{objects}', (objects || []).join(', '));
            }

            if (status === rule.status && rule.kind === '_') {
                return rule.message;
            }
        }

        return null;
    };
}

async function resolveNameFuid(objectId: string, fieldId: string, translationType: string) {
    const translation = await loadTranslationObjectFiled(translationType, objectId, fieldId);

    if (!translation) {
        console.warn(`Translation not found for objectId: ${objectId}, fieldId: ${fieldId}`);
        return 'Not found'
    }

    return translation.find((i: { language: string; }) => i.language === 'en')!.text;
}

export const customHandlers: Record<string, Record<string, ApiErrorHandler>> = {

    "/account": {
        "POST /account/login": createSimpleErrorHandler([
            {status: 401, kind: '_', message: 'Account not found with the provided credentials.'}
        ])
    },

    "/accounts": {

        "GET /accounts/{accountId}": createSimpleErrorHandler([
            {status: 404, kind: 'account_id', message: 'Account not found:\n{objects}'},
        ])

    },

    "/items": {

        "POST /items?category={category}": createSimpleErrorHandler([
            {status: 409, kind: 'item_name', message: 'Item with the given name already exists:\n{objects}'},
            {
                status: 409,
                kind: 'prototype_normalized_id',
                message: 'Item with the given prototype server id already exists:\n{objects}'
            },
        ]),

        "PATCH /items/groups/{group_id}/rename/{new_name}": createSimpleErrorHandler([
            {status: 409, kind: 'group_name', message: 'Group with the given name already exists:\n{objects}'},
            {status: 404, kind: 'group_id', message: 'Group not found:\n{objects}'},
        ]),

        "POST /items/delete": (error, json, status) => {
            if (status === 412) {
                let message = 'Cannot delete – item is in use:\n';
                for (const [id, references] of Object.entries(json as Record<string, ReferencedObject[]>)) {
                    message += `<#>ID:</#> ${id}\n\n`;
                    references.forEach(ref => {
                        message += `<#>Referenced in:</#> ${ref.where}\n`;
                        message += `<#>Group:</#> ${ref.groups!.join(', ')}\n`;
                        message += `<#>Name:</#> ${ref.name_fuid}\n\n`;
                    });
                }
                return message;
            }

            const details = json.details || {};
            if (status === 404 && details.kind === 'item_id') {
                return 'The following item id were not found:\n' + (details.objects || []).join(', ');
            }

            return null;
        }
    },

    "/game-items": {

        "PATCH /game-items/groups/{group_id}/rename/{new_name}": createSimpleErrorHandler([
            {status: 409, kind: 'group_name', message: 'Group with the given name already exists:\n{objects}'},
        ]),

        "PATCH /game-items": createSimpleErrorHandler([
            {status: 409, kind: 'game_item_name', message: 'Game item with the given name already exists:\n{objects}'}
        ]),

        "POST /game-items": createSimpleErrorHandler([
            {status: 409, kind: 'game_item_name', message: 'Game item with the given name already exists:\n{objects}'},
        ]),

        "POST /game-items/delete": async (error, json, status) => {
            if (status === 412) {
                let message = 'Cannot delete – game item is in use:\n';
                for (const [id, references] of Object.entries(json as Record<string, ReferencedObject[]>)) {
                    message += `<#>ID:</#> ${id}\n\n`;

                    for (const ref of references) {
                        const name = await resolveNameFuid(id, ref.name_fuid!, TranslationType.ITEM);

                        message += `<#>Referenced in:</#> ${ref.where}\n`;
                        message += `<#>Group:</#> ${ref.groups!.join(', ')}\n`;
                        message += `<#>Name:</#> ${name}\n\n`;
                    }
                }
                return message;
            }

            const details = json.details || {};
            if (status === 404 && details.kind === 'item_id') {
                return 'The following item id were not found:\n' + (details.objects || []).join(', ');
            }

            return null;
        }
    },

    "/entities": {

        "GET /entities/players/{playerName}/transform":
            createSimpleErrorHandler([
                {status: 404, kind: 'player_transform', message: 'Player not found:\n{objects}'},
            ]),

        "PATCH /entities/groups/reorder?group_id={1:group_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'group_id', message: 'Group not found:\n{objects}'},
            ]),

        "PATCH /entities/groups/{1:group_id}/rename/{2:new_name}":
            createSimpleErrorHandler([
                {status: 409, kind: 'group_name', message: 'Group with the given name already exists:\n{objects}'},
                {status: 404, kind: 'group_id', message: 'Group not found:\n{objects}'},
            ]),

        "POST /entities/mobs":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'group_name',
                    message: 'A mob group with the given name already exists:\n{objects}'
                },
                {status: 409, kind: 'mob_name', message: 'A mob with the given name already exists:\n{objects}'},
            ]),

        "PATCH /entities/mobs":
            createSimpleErrorHandler([
                {status: 409, kind: 'mob_name', message: 'A mob with the given name already exists:\n{objects}'},
                {status: 404, kind: 'mob_id', message: 'Mobs not found:\n{objects}'},
            ]),

        "POST /entities/spawners":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'group_name',
                    message: 'A spawner group with the given name already exists:\n{objects}'
                },
                {
                    status: 409,
                    kind: 'spawner_name',
                    message: 'A spawner with the given name already exists:\n{objects}'
                },
            ]),

        "PATCH /entities/spawners":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'spawner_name',
                    message: 'A spawner with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'mob_id', message: 'The following spawner id were not found:\n{objects}'},
            ]),

        "POST /entities/spawners/delete":
            createSimpleErrorHandler([
                {status: 404, kind: 'spawner_id', message: 'The following spawner id were not found:\n{objects}'},
            ]),

        "POST /entities/mobs/delete":
            (error, json, status) => {
                if (status === 412) {
                    let message = 'Cannot delete – entity is in use:\n';
                    for (const [id, references] of Object.entries(json as Record<string, ReferencedObject[]>)) {
                        message += `<#>ID:</#> ${id}\n\n`;
                        references.forEach(ref => {
                            message += `<#>Referenced in:</#> ${ref.where}\n`;
                            message += `<#>Group:</#> ${ref.group}\n`;
                            message += `<#>Name:</#> ${ref.name}\n`;
                            message += `<#>ID:</#> ${ref.id}\n\n`;
                        });
                    }
                    return message;
                }

                const details = json.details || {};
                if (status === 404 && details.kind === 'mob_id') {
                    return 'The following mob id were not found:\n' + (details.objects)!.join(', ');
                }

                return null;
            },

        "POST /entities/npcs":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'npc_normalized_id',
                    message: 'An NPC with the given server id already exists:\n{objects}'
                },
                {
                    status: 409,
                    kind: 'group_name',
                    message: 'An NPC group with the given name already exists:\n{objects}'
                },
            ]),

        "PATCH /entities/npcs":
            createSimpleErrorHandler([
                {status: 409, kind: 'npc_name', message: 'An NPC with the given name already exists:\n{objects}'},
                {status: 404, kind: 'npc_id', message: 'NPCs not found:\n{objects}'},
            ]),


        "POST /entities/npcs/delete":
            (error, json, status) => {
                if (status === 412) {
                    let message = 'Cannot delete – npc is in use:\n';
                    for (const [id, references] of Object.entries(json as Record<string, ReferencedObject[]>)) {
                        message += `<#>ID:</#> ${id}\n\n`;
                        references.forEach(ref => {
                            message += `<#>Referenced in:</#> ${ref.where}\n`;
                            message += `<#>Group:</#> ${ref.group}\n`;
                            message += `<#>Name:</#> ${ref.name_fuid}\n`;
                            message += `<#>ID:</#> ${ref.id}\n\n`;
                        });
                    }
                    return message;
                }

                const details = json.details || {};
                if (status === 404 && details.kind === 'mob_id') {
                    return 'The following mob id were not found:\n' + (details.objects)!.join(', ');
                }

                return null;
            },

        "GET /entities/npcs/{npcId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'npc_id', message: 'NPC not found:\n{objects}'},
            ]),

    },

    "/quests": {

        "GET /quests/categories/{id}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_category_id', message: 'Quest category not found:\n{objects}'},
            ]),

        "POST /quests/categories":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_category_name',
                    message: 'A quest category with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'quest_mapping_id', message: 'Given quest mapping not found:\n{objects}'},
            ]),

        "PATCH /quests/categories":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_category_name',
                    message: 'A quest category with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'quest_category_id', message: 'Quest categories not found:\n{objects}'},
            ]),

        "POST /quests/categories/delete":
            (error, json, status) => {
                if (status === 412) {
                    let message = 'Cannot delete – quest category is in use:\n';
                    for (const [id, references] of Object.entries(json as Record<string, ReferencedObject[]>)) {
                        message += `<#>ID:</#> ${id}\n\n`;
                        references.forEach(ref => {
                            message += `<#>Referenced in:</#> ${ref.where}\n`;
                            message += `<#>Group:</#> ${ref.group}\n`;
                            message += `<#>Name:</#> ${ref.name_fuid}\n`;
                            message += `<#>ID:</#> ${ref.id}\n\n`;
                        });
                    }
                    return message;
                }

                const details = json.details || {};
                if (status === 404 && details.kind === 'quest_category_id') {
                    return 'The following quest category id were not found:\n' + (details.objects)!.join(', ');
                }

                return null;
            },

        "PATCH /quests/categories/groups/{groupId}/rename/{newName}":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_category_group_id',
                    message: 'A quest category with the given id not found:\n{objects}'
                },
                {
                    status: 404,
                    kind: 'quest_category_group_name',
                    message: 'A quest category group with the given name already exists:\n{objects}'
                },
            ]),

        "PATCH /quests/categories/groups/reorder?group_id={1:group_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_category_group_id', message: 'Quest category group not found:\n{objects}'},
            ]),

        "PATCH /quests/categories/reorder?category={1:mapping_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_category_id', message: 'Quest category not found:\n{objects}'},
            ]),


        "GET /quests/mappings/{mappingId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_mapping_id', message: 'Quest mapping not found:\n{objects}'},
            ]),

        "POST /quests/mappings":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_mapping_name',
                    message: 'A quest mapping with the given name already exists:\n{objects}'
                },
            ]),

        "PATCH /quests/mappings":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_mapping_name',
                    message: 'A quest mapping with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'quest_mapping_id', message: 'Quest mappings not found:\n{objects}'},
            ]),

        "PATCH /quests/mappings/groups/{groupId}/rename/{newName}":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_mapping_group_id',
                    message: 'A quest mapping with the given id not found:\n{objects}'
                },
                {
                    status: 404,
                    kind: 'quest_mapping_group_name',
                    message: 'A quest mapping group with the given name already exists:\n{objects}'
                },
            ]),

        "PATCH /quests/mappings/groups/reorder?group_id={1:group_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_mapping_group_id', message: 'Quest mapping group not found:\n{objects}'},
            ]),

        "PATCH /quests/mappings/reorder?mapping_id={1:mapping_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_mapping_id', message: 'Quest mapping not found:\n{objects}'},
            ]),


        "POST /quests/mappings/delete":
            createSimpleErrorHandler([
                {
                    status: 404,
                    kind: 'quest_mapping_id',
                    message: 'The following quest mapping id were not found:\n{objects}'
                },
            ]),

        "GET /quests/{questId}/status":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_id', message: 'Quest not found:\n{objects}'},
            ]),

        "GET /quests/{questId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_id', message: 'Quest not found:\n{objects}'},
            ]),

        "PATCH /quests/":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_id', message: 'Quests not found:\n{objects}'},
            ]),

        "POST /quests/delete":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_id', message: 'The following quest id were not found:\n{objects}'},
            ]),

        "POST /quests/generation/generate":
            createSimpleErrorHandler([
                {status: 409, kind: 'quest_id', message: 'A quest with the given id already exists:\n{objects}'},
                {status: 404, kind: 'quest_category_id', message: 'Given quest category not found:\n{objects}'},
                {status: 412, condition: 'mappings-valid', message: 'Cannot generate quests - Invalid mappings'}
            ]),

        "POST /quests/generation/regenerate":
            createSimpleErrorHandler([
                {status: 404, kind: 'quest_id', message: 'Quest not found:\n{objects}'},
            ]),


        "DELETE /quests/groups/{groupId}":
            (error, json, status) => {
                if (status === 412) {
                    return 'Cannot delete – quest group is not empty';
                }

                return null;
            },

        "PATCH /quests/groups/{groupId}/rename/{newName}":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'quest_group_name',
                    message: 'Quest group with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'quest_group_id', message: 'Quest group not found:\n{objects}'},
            ]),
    },

    "/wiki": {
        "PATCH /wiki/categories/reorder?category_id={1:category_id}&pos={2:pos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'category_id', message: 'Category not found:\n{objects}'},
            ]),

        "PATCH /wiki/categories/{1:category_id}/rename/{2:new_name}":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'category_name',
                    message: 'Category with the given name already exists:\n{objects}'
                },
                {status: 404, kind: 'category_id', message: 'Category not found:\n{objects}'},
            ]),

        "DELETE /wiki/categories/{1:category_id}":
            createSimpleErrorHandler([
                {status: 404, kind: 'category_id', message: 'Category not found:\n{objects}'},
            ]),

        "POST /wiki/languages":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'lang_name',
                    message: 'Language with the given name already exists:\n{objects}'
                },
                {status: 409, kind: 'lang_cc', message: 'Language with the given code already exists:\n{objects}'},
            ]),

        "PATCH /wiki/languages":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'lang_name',
                    message: 'Language with the given name already exists:\n{objects}'
                },
                {status: 409, kind: 'lang_cc', message: 'Language with the given code already exists:\n{objects}'},
                {status: 404, kind: 'lang_id', message: 'Languages not found:\n{objects}'},
            ]),

        "POST /wiki/languages/delete":
            (error, json, status) => {
                const details = json.details || {};

                if (status === 404 && details.kind === 'lang_cc') {
                    return 'The following language codes were not found:\n' + details.objects!.join(', ');
                }

                if (status === 412 && Array.isArray(json)) {
                    return 'Cannot delete – the following language codes are in use:\n' + json.join(', ');
                }

                return null;
            },

        "GET /wiki/content/{1:category_id}":
            createSimpleErrorHandler([
                {status: 404, kind: 'category_id', message: 'Category not found:\n{objects}'},
            ]),

        "PUT /wiki/content/{category_type}/{country_code}":
            createSimpleErrorHandler([
                {status: 404, kind: 'category_id', message: 'Category not found:\n{objects}'},
            ]),

    },

    "/settings": {

        "GET `settings/network-infra/regions/{1:region_uid}":
            createSimpleErrorHandler([
                {status: 404, kind: 'region_id', message: 'Region not found:\n{objects}'},
            ]),

        "POST /settings/network-infra/regions":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'region_name',
                    message: 'Regions with the given names already exist:\n{objects}'
                },
            ]),

        "PATCH /settings/network-infra/regions":
            createSimpleErrorHandler([
                {
                    status: 409,
                    kind: 'region_name',
                    message: 'Regions with the given names already exist:\n{objects}'
                },
                {status: 404, kind: 'region_id', message: 'Regions not found:\n{objects}'},
            ]),

        "DELETE /settings/network-infra/regions/{regionId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'region_id', message: 'Region not found:\n{objects}'},
            ]),

        "PATCH /settings/network-infra/regions/{regionId}/reorder?new_pos={newPos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'region_id', message: 'Region not found:\n{objects}'},
            ]),

        "DELETE /settings/ai-tokens-limits/exceptions/{exceptionId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'ai_tokens_limits_exception_id', message: 'Exception not found:\n{objects}'},
            ]),

        "PATCH /settings/ai-tokens-limits/reorder?exception={exceptionId}&pos={newPos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'ai_tokens_limits_exception_id', message: 'Exception not found:\n{objects}'},
            ]),

        "DELETE /settings/ai-tokens-limits/exceptions/groups/{groupId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'ai_tokens_limits_exception_id', message: 'Exception not found:\n{objects}'},
            ]),

        "PATCH /settings/ai-tokens-limits/exceptions/groups/reorder?group={groupId}&pos={newPos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'ai_tokens_limits_exception_group_id', message: 'Group not found:\n{objects}'},
            ]),

        "PATCH /settings/ai-tokens-limits/exceptions/groups/{groupId}/rename/{newName}":
            createSimpleErrorHandler([
                {status: 404, kind: 'ai_tokens_limits_exception_group_id', message: 'Group not found:\n{objects}'},
                {
                    status: 409,
                    kind: 'ai_tokens_limits_exception_group_name',
                    message: 'Group name already exists:\n{objects}'
                },
            ]),


    },

    "/discord": {

        "POST /message/send/{id}/to/{channelId}":
            createSimpleErrorHandler([
                {status: 404, kind: 'channel_id_missing', message: 'Discord channel ID is missing or invalid.'},
                {status: 404, kind: 'draft_id_missing', message: 'Discord message draft ID is missing or invalid.'},

                {
                    status: 404,
                    kind: 'invalid_channel_type',
                    message: 'The specified channel ID does not correspond to a text channel.'
                },

                {
                    status: 404,
                    kind: 'discord_channel_not_found',
                    message: 'Discord channel not found or the bot does not have access to it.'
                },
                {status: 404, kind: 'draft_not_found', message: 'Discord message draft not found.'},

                {
                    status: 404,
                    kind: 'default_content_required',
                    message: 'Default content in lang {objects} is required.'
                },
            ]),

        "POST /message/save":
            createSimpleErrorHandler([
                {status: 404, kind: 'message_draft_already_exists', message: 'A message draft with name: {objects} already exists.'},

            ]),

        "PATCH /message/update":
            createSimpleErrorHandler([
                {status: 404, kind: 'draft_not_found', message: 'Discord message draft not found.'},
            ]),
    },

    "/translations": {

        "PATCH /translations/languages/{language}/reorder?new_pos={newPos}":
            createSimpleErrorHandler([
                {status: 404, kind: 'lang_code', message: 'Language not found:\n{objects}'},
            ]),

        "PUT /translations/static":
            createSimpleErrorHandler([
                {status: 404, kind: 'lang_code', message: 'Language not found:\n{objects}'},
            ]),

        "POST /translations/static/delete":
            createSimpleErrorHandler([
                {status: 404, kind: 'static_translation_key', message: 'Language not found:\n{objects}'},
            ]),

        "PATCH /translations/static/{oldKey}/rename/{newKey}":
            createSimpleErrorHandler([
                {status: 409, kind: 'static_translation_key', message: 'Translation key already exists:\n{objects}'},
                {status: 404, kind: 'static_translation_key', message: 'Translation key not found:\n{objects}'},
            ]),

        "PUT /translations/dynamic/{category}":
            createSimpleErrorHandler([
                {status: 404, kind: 'lang_code', message: 'Language not found:\n{objects}'},
            ])

    }
};